import { sanitizeWidgetParams } from '@core/params/helpers/sanitizeWidgetParams'
import type { IAnyWidgetParams } from '@core/types'

import { PARAM_PREFIX } from './constants'

// Global flag to track if all scripts have been parsed
let allScriptsParsed = false

/**
 * Extracts the param key without the `fwparam_` prefix.
 * @param input The original key
 * @returns The param key without `fwparam_` prefix
 */
function extractParamKey(input: string): string {
  if (input.startsWith(PARAM_PREFIX)) {
    return input.substring(PARAM_PREFIX.length)
  }
  return input
}

/**
 * Gets the current script element, handling both regular scripts and ES modules.
 * @param importMetaUrl Optional import.meta.url for ES modules
 * @returns The current script element or null
 */
function getCurrentScript(importMetaUrl?: string): HTMLScriptElement | null {
  // For regular scripts, document.currentScript is available
  if (document.currentScript && 'src' in document.currentScript) {
    return document.currentScript as HTMLScriptElement
  }

  // For ES modules, document.currentScript is null, so we need to find it using import.meta.url
  if (importMetaUrl) {
    const scripts = document.querySelectorAll('script[src]')
    for (const script of scripts) {
      const scriptEl = script as HTMLScriptElement
      if (scriptEl.src === importMetaUrl) {
        return scriptEl
      }
    }
  }

  return null
}

/**
 * Parses current script dataset attributes and URL query parameters, returning them sanitized.
 * Removes the `fwparam_` prefix from keys before sanitization.
 *
 * @param importMetaUrl Optional import.meta.url for ES modules where document.currentScript is null
 * @returns Sanitized widget parameters from the current script's dataset and URL query parameters
 * @throws Error if no current script is found
 */
export function parseScriptConfig(importMetaUrl?: string): IAnyWidgetParams {
  const currentScript = getCurrentScript(importMetaUrl)

  if (!currentScript) {
    throw new Error(
      'No current script found. For ES modules, pass import.meta.url as parameter.',
    )
  }

  const rawParams: Record<string, string> = {}

  // Parse URL query parameters first
  try {
    const scriptURL = new URL(currentScript.src)
    if (scriptURL.search) {
      for (const [key, value] of scriptURL.searchParams.entries()) {
        rawParams[key] = value
      }
    }
  } catch (e) {
    // Silently fail if URL parsing fails
  }

  // Parse all dataset attributes (these will override query params if there are conflicts)
  for (const key of Object.keys(currentScript.dataset)) {
    const value = currentScript.dataset[key]
    if (typeof value !== 'undefined') {
      // Remove fwparam_ prefix if present
      const paramKey = extractParamKey(key)
      rawParams[paramKey] = value
    }
  }

  // Sanitize the parameters using the existing sanitization logic
  return sanitizeWidgetParams(rawParams)
}

/**
 * Parses all scripts on the page with data-fwparam_ attributes exactly once.
 * This function will only execute once per page load to avoid duplicate processing.
 *
 * @returns Sanitized widget parameters merged from all scripts with data-fwparam_ attributes
 */
export function parseAllScriptsConfig(): IAnyWidgetParams {
  // Return empty object if already parsed
  if (allScriptsParsed) {
    return {}
  }

  // Mark as parsed to prevent duplicate execution
  allScriptsParsed = true

  const allRawParams: Record<string, string> = {}

  // Find all script elements and filter for those with data-fwparam_ attributes
  const allScripts = document.querySelectorAll('script')

  for (const scriptElement of allScripts) {
    const script = scriptElement as HTMLScriptElement

    // Check if this script has any data-fwparam_ attributes
    const hasFwparamAttributes = Object.keys(script.dataset).some((key) =>
      key.startsWith(PARAM_PREFIX),
    )

    if (!hasFwparamAttributes) {
      continue
    }

    // Parse URL query parameters if script has src
    if (script.src) {
      try {
        const scriptURL = new URL(script.src)
        if (scriptURL.search) {
          for (const [key, value] of scriptURL.searchParams.entries()) {
            // Only collect parameters that don't already exist (first script wins for conflicts)
            if (!(key in allRawParams)) {
              allRawParams[key] = value
            }
          }
        }
      } catch (e) {
        // Silently fail if URL parsing fails
      }
    }

    // Parse dataset attributes, focusing on fwparam_ prefixed ones
    for (const key of Object.keys(script.dataset)) {
      const value = script.dataset[key]
      if (typeof value !== 'undefined' && key.startsWith(PARAM_PREFIX)) {
        // Remove fwparam_ prefix
        const paramKey = extractParamKey(key)
        // Dataset attributes take precedence over URL params
        allRawParams[paramKey] = value
      }
    }
  }

  // Sanitize the parameters using the existing sanitization logic
  return sanitizeWidgetParams(allRawParams)
}

/**
 * Resets the parsing state for testing purposes.
 * This function should only be used in tests.
 */
export function resetAllScriptsParsingState(): void {
  allScriptsParsed = false
}
